import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';

export default function Privacy() {
  const { t, language } = useLanguage();

  const content = {
    pt: {
      intro: 'A tua privacidade é importante para nós. Esta política explica como recolhemos, usamos e protegemos os teus dados.',
      sections: [
        {
          title: '1. Dados que Recolhemos',
          text: 'Recolhemos informações que forneces diretamente: nome, email, informações de pagamento. Também recolhemos dados de utilização: SharpSlips gerados, preferências de apostas, e dados analíticos anónimos.'
        },
        {
          title: '2. Como Usamos os Dados',
          text: 'Utilizamos os teus dados para: fornecer e melhorar o serviço, processar pagamentos, enviar comunicações importantes, personalizar a experiência e analisar padrões de utilização.'
        },
        {
          title: '3. Partilha de Dados',
          text: 'Não vendemos os teus dados pessoais. Partilhamos dados apenas com: processadores de pagamento (Stripe), fornecedores de autenticação (Google OAuth), e serviços de análise agregada.'
        },
        {
          title: '4. Segurança',
          text: 'Implementamos medidas de segurança padrão da indústria incluindo encriptação, autenticação segura e monitorização contínua para proteger os teus dados.'
        },
        {
          title: '5. Cookies',
          text: 'Utilizamos cookies essenciais para autenticação e preferências de sessão. Podes gerir as preferências de cookies através do teu browser.'
        },
        {
          title: '6. Os Teus Direitos',
          text: 'Tens o direito de: aceder aos teus dados, corrigir informações incorretas, solicitar eliminação da conta, e exportar os teus dados. Contacta-nos para exercer estes direitos.'
        },
        {
          title: '7. Retenção de Dados',
          text: 'Mantemos os teus dados enquanto a conta estiver ativa. Após eliminação, os dados são removidos dentro de 30 dias, exceto onde exigido por lei.'
        },
        {
          title: '8. Alterações à Política',
          text: 'Podemos atualizar esta política periodicamente. Notificar-te-emos de alterações significativas por email.'
        }
      ]
    },
    en: {
      intro: 'Your privacy is important to us. This policy explains how we collect, use, and protect your data.',
      sections: [
        {
          title: '1. Data We Collect',
          text: 'We collect information you provide directly: name, email, payment information. We also collect usage data: SharpSlips generated, betting preferences, and anonymous analytics.'
        },
        {
          title: '2. How We Use Data',
          text: 'We use your data to: provide and improve the service, process payments, send important communications, personalize your experience, and analyze usage patterns.'
        },
        {
          title: '3. Data Sharing',
          text: 'We do not sell your personal data. We share data only with: payment processors (Stripe), authentication providers (Google OAuth), and aggregate analytics services.'
        },
        {
          title: '4. Security',
          text: 'We implement industry-standard security measures including encryption, secure authentication, and continuous monitoring to protect your data.'
        },
        {
          title: '5. Cookies',
          text: 'We use essential cookies for authentication and session preferences. You can manage cookie preferences through your browser.'
        },
        {
          title: '6. Your Rights',
          text: 'You have the right to: access your data, correct inaccurate information, request account deletion, and export your data. Contact us to exercise these rights.'
        },
        {
          title: '7. Data Retention',
          text: 'We retain your data while your account is active. After deletion, data is removed within 30 days, except where required by law.'
        },
        {
          title: '8. Policy Changes',
          text: 'We may update this policy periodically. We will notify you of significant changes via email.'
        }
      ]
    },
    es: {
      intro: 'Tu privacidad es importante para nosotros. Esta política explica cómo recopilamos, usamos y protegemos tus datos.',
      sections: [
        {
          title: '1. Datos que Recopilamos',
          text: 'Recopilamos información que proporcionas directamente: nombre, correo electrónico, información de pago. También recopilamos datos de uso.'
        },
        {
          title: '2. Cómo Usamos los Datos',
          text: 'Usamos tus datos para: proporcionar y mejorar el servicio, procesar pagos, enviar comunicaciones importantes.'
        },
        {
          title: '3. Compartición de Datos',
          text: 'No vendemos tus datos personales. Compartimos datos solo con procesadores de pago y proveedores de autenticación.'
        },
        {
          title: '4. Seguridad',
          text: 'Implementamos medidas de seguridad estándar de la industria incluyendo encriptación y autenticación segura.'
        },
        {
          title: '5. Cookies',
          text: 'Usamos cookies esenciales para autenticación y preferencias de sesión.'
        },
        {
          title: '6. Tus Derechos',
          text: 'Tienes derecho a: acceder a tus datos, corregir información incorrecta, solicitar eliminación de cuenta.'
        },
        {
          title: '7. Retención de Datos',
          text: 'Mantenemos tus datos mientras tu cuenta esté activa.'
        },
        {
          title: '8. Cambios en la Política',
          text: 'Podemos actualizar esta política periódicamente.'
        }
      ]
    },
    fr: {
      intro: "Votre vie privée est importante pour nous. Cette politique explique comment nous collectons et protégeons vos données.",
      sections: [
        {
          title: '1. Données Collectées',
          text: "Nous collectons les informations que vous fournissez directement: nom, email, informations de paiement."
        },
        {
          title: '2. Utilisation des Données',
          text: "Nous utilisons vos données pour fournir et améliorer le service, traiter les paiements."
        },
        {
          title: '3. Partage des Données',
          text: "Nous ne vendons pas vos données personnelles."
        },
        {
          title: '4. Sécurité',
          text: "Nous mettons en œuvre des mesures de sécurité standard de l'industrie."
        },
        {
          title: '5. Cookies',
          text: "Nous utilisons des cookies essentiels pour l'authentification."
        },
        {
          title: '6. Vos Droits',
          text: "Vous avez le droit d'accéder à vos données et de demander leur suppression."
        },
        {
          title: '7. Conservation des Données',
          text: "Nous conservons vos données tant que votre compte est actif."
        },
        {
          title: '8. Modifications de la Politique',
          text: "Nous pouvons mettre à jour cette politique périodiquement."
        }
      ]
    },
    de: {
      intro: 'Deine Privatsphäre ist uns wichtig. Diese Richtlinie erklärt, wie wir deine Daten sammeln und schützen.',
      sections: [
        {
          title: '1. Erhobene Daten',
          text: 'Wir erfassen Informationen, die du direkt angibst: Name, E-Mail, Zahlungsinformationen.'
        },
        {
          title: '2. Datennutzung',
          text: 'Wir nutzen deine Daten, um den Service bereitzustellen und zu verbessern.'
        },
        {
          title: '3. Datenweitergabe',
          text: 'Wir verkaufen deine persönlichen Daten nicht.'
        },
        {
          title: '4. Sicherheit',
          text: 'Wir setzen branchenübliche Sicherheitsmaßnahmen ein.'
        },
        {
          title: '5. Cookies',
          text: 'Wir verwenden wesentliche Cookies für die Authentifizierung.'
        },
        {
          title: '6. Deine Rechte',
          text: 'Du hast das Recht, auf deine Daten zuzugreifen und deren Löschung zu beantragen.'
        },
        {
          title: '7. Datenspeicherung',
          text: 'Wir bewahren deine Daten auf, solange dein Konto aktiv ist.'
        },
        {
          title: '8. Richtlinienänderungen',
          text: 'Wir können diese Richtlinie regelmäßig aktualisieren.'
        }
      ]
    }
  };

  const currentContent = content[language] || content.en;

  return (
    <div className="min-h-screen bg-background grain-overlay">
      {/* Header */}
      <nav className="border-b border-white/5 backdrop-blur-md bg-background/80 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft weight="bold" />
            <span>SharpBetAI</span>
          </Link>
          <LanguageSelector />
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-heading font-black text-white mb-2">{t('privacyTitle')}</h1>
        <p className="text-white/40 text-sm mb-8">{t('termsLastUpdate')}: 30 March 2026</p>

        <p className="text-white/70 mb-8 text-lg">{currentContent.intro}</p>

        <div className="space-y-8">
          {currentContent.sections.map((section, idx) => (
            <div key={idx} className="glass-surface p-6 rounded-sm">
              <h2 className="text-xl font-heading font-bold text-white mb-3">{section.title}</h2>
              <p className="text-white/70 leading-relaxed">{section.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link 
            to="/"
            className="inline-block px-8 py-3 bg-primary text-background font-bold rounded-full hover:bg-primary/90 transition-all"
          >
            {t('backToDashboard')}
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-white/40 text-sm">
          <p>{t('footerCopyright')}</p>
        </div>
      </footer>
    </div>
  );
}
